import React, { useRef, useState, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './Tooltip.css';

interface TooltipProps {
  text: string;
  children: React.ReactNode;
  position?: 'top' | 'bottom' | 'left' | 'right';
  align?: 'left' | 'center' | 'right';
  contentClassName?: string;
  /**
   * Render the tooltip into document.body via a portal. Use this when the
   * trigger lives inside a scroll/overflow container (e.g. a DataTable cell)
   * that would otherwise clip the absolute-positioned tooltip.
   */
  usePortal?: boolean;
}

const Tooltip: React.FC<TooltipProps> = ({
  text,
  children,
  position = 'bottom',
  align = 'center',
  contentClassName = '',
  usePortal = false,
}) => {
  const getPositionClass = () => {
    switch (position) {
      case 'top':
        return 'bottom-full mb-2';
      case 'bottom':
        return 'top-full mt-2';
      case 'left':
        return 'right-full mr-2';
      case 'right':
        return 'left-full ml-2';
      default:
        return 'top-full mt-2';
    }
  };

  const getAlignmentClass = () => {
    if (position === 'top' || position === 'bottom') {
      switch (align) {
        case 'left':
          return 'left-0';
        case 'right':
          return 'right-0';
        case 'center':
          return 'left-1/2 -translate-x-1/2';
        default:
          return 'left-1/2 -translate-x-1/2';
      }
    }
    return '';
  };

  if (usePortal) {
    return <PortalTooltip text={text} position={position} align={align} contentClassName={contentClassName}>{children}</PortalTooltip>;
  }

  return (
    <div className="has-tooltip relative flex items-center">
      {children}
      <div
        className={`tooltip max-w-xs rounded-md bg-gray-800 p-2 text-xs text-white whitespace-pre-line break-words ${getPositionClass()} ${getAlignmentClass()} ${contentClassName}`}
      >
        {text}
      </div>
    </div>
  );
};

/**
 * Portal-rendered variant: tooltip is positioned via fixed coordinates and
 * mounted on document.body so ancestor `overflow: hidden`/`overflow: auto`
 * cannot clip it. Coordinates are recomputed on hover.
 */
const PortalTooltip: React.FC<{
  text: string;
  children: React.ReactNode;
  position: 'top' | 'bottom' | 'left' | 'right';
  align: 'left' | 'center' | 'right';
  contentClassName: string;
}> = ({ text, children, position, align, contentClassName }) => {
  const triggerRef = useRef<HTMLSpanElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  const updatePosition = useCallback(() => {
    const trig = triggerRef.current;
    const tip = tooltipRef.current;
    if (!trig || !tip) return;
    const tr = trig.getBoundingClientRect();
    const tw = tip.offsetWidth;
    const th = tip.offsetHeight;
    const gap = 8;
    let top = 0;
    let left = 0;
    switch (position) {
      case 'top':
        top = tr.top - th - gap;
        left = align === 'left' ? tr.left : align === 'right' ? tr.right - tw : tr.left + tr.width / 2 - tw / 2;
        break;
      case 'bottom':
        top = tr.bottom + gap;
        left = align === 'left' ? tr.left : align === 'right' ? tr.right - tw : tr.left + tr.width / 2 - tw / 2;
        break;
      case 'left':
        top = tr.top + tr.height / 2 - th / 2;
        left = tr.left - tw - gap;
        break;
      case 'right':
        top = tr.top + tr.height / 2 - th / 2;
        left = tr.right + gap;
        break;
    }
    // Clamp to viewport so the tooltip stays visible at table edges.
    const vw = window.innerWidth;
    const margin = 4;
    if (left < margin) left = margin;
    if (left + tw > vw - margin) left = vw - tw - margin;
    setCoords({ top, left });
  }, [position, align]);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open, updatePosition]);

  return (
    <>
      <span
        ref={triggerRef}
        className="inline-flex items-center"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
      >
        {children}
      </span>
      {open && createPortal(
        <div
          ref={tooltipRef}
          role="tooltip"
          style={{ top: coords.top, left: coords.left, position: 'fixed' }}
          className={`z-[1000] max-w-xs rounded-md bg-gray-800 p-2 text-xs text-white whitespace-pre-line break-words pointer-events-none ${contentClassName}`}
        >
          {text}
        </div>,
        document.body,
      )}
    </>
  );
};

export default Tooltip;
